/**
 * All Products banner — bottom social/lifestyle marquee with hero hover swap.
 * Scoped to .muskle-ccb--all-logo only.
 */
(function () {
  const ROOT_SEL = ".muskle-ccb--all-logo [data-muskle-all-social-strip]";
  const DATA_URL = window.MUSKLE_ALL_SOCIAL_STRIP_DATA_URL;
  const HERO_CDN_WIDTH = 192;
  const TRANSITION_MS = 220;
  const IDLE_TIMEOUT_MS = 1500;
  const IDLE_FALLBACK_MS = 800;

  if (!DATA_URL) return;

  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  function cdnSrc(base, width) {
    if (!base) return "";
    return `${base.split("?")[0]}?width=${width}`;
  }

  function makeSocialImg(src, width, height) {
    const img = document.createElement("img");
    const w = width;
    const url = cdnSrc(src, w);
    img.src = url;
    img.srcset = `${cdnSrc(src, w)} ${w}w, ${cdnSrc(src, w * 2)} ${w * 2}w`;
    img.sizes = `${w}px`;
    img.alt = "";
    img.decoding = "async";
    img.width = w;
    img.height = height;
    img.dataset.role = "social";
    img.dataset.socialLoaded = "1";
    return img;
  }

  function ensureSocialLoaded(img) {
    if (!img || img.dataset.socialLoaded === "1") return;
    if (img.dataset.src && !img.getAttribute("src")) img.src = img.dataset.src;
    if (img.dataset.srcset && !img.getAttribute("srcset")) img.srcset = img.dataset.srcset;
    img.dataset.socialLoaded = "1";
  }

  function hydrateSocialImages(root) {
    const imgs = root.querySelectorAll('img[data-role="social"]');
    if (!imgs.length) return;
    if (!("IntersectionObserver" in window)) {
      imgs.forEach(ensureSocialLoaded);
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            ensureSocialLoaded(e.target);
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "160px 0px" }
    );
    imgs.forEach((img) => io.observe(img));
  }

  function makeHeroPlaceholder(width, height, heroUrl) {
    const img = document.createElement("img");
    const heroSrc = cdnSrc(heroUrl, HERO_CDN_WIDTH);
    img.dataset.role = "hero";
    img.src = heroSrc;
    img.srcset = `${heroSrc} ${HERO_CDN_WIDTH}w, ${cdnSrc(heroUrl, HERO_CDN_WIDTH * 2)} ${HERO_CDN_WIDTH * 2}w`;
    img.dataset.heroSrc = heroSrc;
    img.dataset.heroLoaded = "1";
    img.sizes = `${width}px`;
    img.alt = "";
    img.decoding = "async";
    img.width = HERO_CDN_WIDTH;
    img.height = height;
    return img;
  }

  function ensureHeroLoaded(heroImg) {
    if (!heroImg || heroImg.dataset.heroLoaded === "1" || !heroImg.dataset.heroSrc) return;
    heroImg.src = heroImg.dataset.heroSrc;
    if (heroImg.dataset.heroSrcset) heroImg.srcset = heroImg.dataset.heroSrcset;
    heroImg.dataset.heroLoaded = "1";
  }

  function buildThumb(item, thumbW) {
    const social = item.social_image || item.url;
    const hero = item.hero_image;
    const thumbH = Math.round(thumbW * 0.72);

    const el = document.createElement("div");
    el.className = "muskle-ccb__social-thumb";
    el.dataset.productHandle = item.handle || "";

    const inner = document.createElement("div");
    inner.className = "muskle-ccb__social-thumb-inner";

    inner.appendChild(makeSocialImg(social, thumbW, thumbH));
    if (hero) {
      inner.appendChild(makeHeroPlaceholder(thumbW, thumbH, hero));
    }

    el.appendChild(inner);
    return el;
  }

  function bindHover(track) {
    if (!canHover) return;

    let hoverCount = 0;
    const pause = () => track.classList.add("is-paused");
    const resume = () => {
      if (hoverCount === 0) track.classList.remove("is-paused");
    };

    track.querySelectorAll(".muskle-ccb__social-thumb").forEach((thumb) => {
      thumb.addEventListener("mouseenter", () => {
        const heroImg = thumb.querySelector('img[data-role="hero"]');
        ensureHeroLoaded(heroImg);
        hoverCount += 1;
        thumb.classList.add("is-hovered");
        pause();
      });
      thumb.addEventListener("mouseleave", () => {
        hoverCount = Math.max(0, hoverCount - 1);
        thumb.classList.remove("is-hovered");
        resume();
      });
    });
  }

  function fillTrack(track, items, thumbW, { duplicate = true } = {}) {
    track.replaceChildren();
    const fragA = document.createDocumentFragment();
    for (const item of items) {
      fragA.appendChild(buildThumb(item, thumbW));
    }
    track.appendChild(fragA);
    // Second copy is only needed for seamless desktop/tablet marquee loop.
    if (duplicate) {
      const fragB = document.createDocumentFragment();
      for (const item of items) {
        fragB.appendChild(buildThumb(item, thumbW));
      }
      track.appendChild(fragB);
    }
  }

  async function init() {
    const root = document.querySelector(ROOT_SEL);
    if (!root || root.dataset.muskleSocialInit === "1") return;

    let data;
    try {
      const res = await fetch(DATA_URL, { credentials: "same-origin" });
      data = await res.json();
    } catch {
      return;
    }

    const items = (data?.images || []).slice(0, 20);
    if (!items.length) return;

    const track = root.querySelector("[data-muskle-all-social-track]");
    if (!track) return;

    const isPhone = window.matchMedia("(max-width: 749px)").matches;
    const thumbW = isPhone ? 58 : 96;
    fillTrack(track, items, thumbW, { duplicate: !isPhone });
    hydrateSocialImages(root);
    bindHover(track);

    root.style.setProperty("--muskle-social-hover-ms", `${TRANSITION_MS}ms`);
    root.dataset.muskleSocialInit = "1";
  }

  function scheduleInit() {
    const run = () => {
      init();
    };
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
    } else {
      setTimeout(run, IDLE_FALLBACK_MS);
    }
  }

  function initWhenVisible() {
    const root = document.querySelector(ROOT_SEL);
    if (!root || root.dataset.muskleSocialInit === "1") return;

    const start = () => {
      if (root.dataset.muskleSocialScheduled === "1") return;
      root.dataset.muskleSocialScheduled = "1";
      scheduleInit();
    };

    if (!("IntersectionObserver" in window)) {
      scheduleInit();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          start();
        }
      },
      { rootMargin: "320px 0px" }
    );
    io.observe(root);
    setTimeout(() => {
      io.disconnect();
      start();
    }, 15000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWhenVisible, { once: true });
  } else {
    initWhenVisible();
  }
})();
